import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ThirdPartyConnectModalContainer from '@app/web/src/containers/ThirdPartyConnectModalContainer';

import { useDiscountBanner } from '@lib/core/banners/hooks/discountBanner';
import { useCharacters } from '@lib/core/characters/hooks';
import { selectEssenceGroupsLoading } from '@lib/core/essences/selectors/groups';
import { useRecycling, useWellness } from '@lib/core/productAttributes/hooks';
import { useProducts } from '@lib/core/products/hooks/useProducts';
import { useResultCatalog } from '@lib/core/products/hooks/useResultCatalog';
import { TProductInstance } from '@lib/core/products/types';
import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { useRetailerLocation } from '@lib/core/retailers/hooks/useRetailerLocation';
import { RETAILER_TAG_EXTERNAL_REFERENCE_ID } from '@lib/core/retailers/utils/consts';
import { ApiQueries, BrowserUrlConsts, isApplicationKiosk } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { useScrollToTop } from '@lib/core/service/hooks/useScrollToTop';
import { useTastePathJourneyData } from '@lib/core/service/hooks/useTastePathJourneyData';
import { useThirdParty } from '@lib/core/service/hooks/useThirdParty';
import {
  setIsTasteIdGuideShown,
  setIsTastePathOnboardingShown,
  setServiceLocale,
  setShouldHideDownloadAppCard,
  toggleShowScanBarcode,
} from '@lib/core/service/slices';
import { isAppInIframe, prependBasename } from '@lib/core/service/utils';
import { useProductList, useProductRate, useUser } from '@lib/core/users/hooks';
import { useFidelityCard } from '@lib/core/users/hooks/useFidelityCard';
import { isFromSharedLink } from '@lib/tools/comms/utils';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { useDiscoveryQuiz } from '@lib/tools/discoveryQuiz/hooks';
import { useExploreLocations } from '@lib/tools/explore/hooks';
import { actionSelectGameProduct } from '@lib/tools/gamification/slices';
import { useLocaleReady } from '@lib/tools/locale/providers/LocaleProvider';
import { useModals } from '@lib/tools/modals/hooks';
import { fetchPromotionProducts, resetPromotionProducts } from '@lib/tools/promotionProducts/slices';
import { RouteUtils } from '@lib/tools/routes';
import { MODALS, PROMOTION_LABEL_SLUG } from '@lib/tools/shared/helpers/consts';
import { useToastMessage } from '@lib/tools/toastMessage/hooks';
import { useAddons, useTypedDispatch } from '@lib/tools/views/hooks';
import { PAGES } from '@lib/tools/views/urls';

import { VoucherBanner } from '@components/web/src/atoms/Banner/VoucherBanner/VoucherBanner';
import { HeaderFooterWidget } from '@components/web/src/atoms/HeaderFooter';
import { RootElement } from '@components/web/src/atoms/RootElement/rootElement';
import {
  LOADING_SPINNER_VARIANTS,
  LoadingSpinner,
} from '@components/web/src/templates/Loading/LoadingSpinner/LoadingSpinner';
import { DiscoveryQuizModal } from '@components/web/src/templates/Modals/DiscoveryQuizModal/DiscoveryQuizModal';
import { ModalWrapper } from '@components/web/src/templates/Modals/ModalWrapper/ModalWrapper';
import { ScannerBarcode } from '@components/web/src/templates/Tutorials/ScannerBarcode/ScannerBarcode';

export const HocWrapper = ({ children }) => {
  useScrollToTop();
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  const isLocaleReady = useLocaleReady();

  const { retailerLanguages, isAnonymousRetailerAccess, retailerName, retailerTags } = useRetailer();
  const { retailerLocationProducerId, retailerLocationName, retailerLocationCustomStyle } = useRetailerLocation();

  const { openModal, isDiscoveryQuizModalOpened, handleToggleModal } = useModals();
  const {
    isDisableHomeAddon,
    isExternalCharacterPage,
    isEnableAppBanner,
    isEnableTastePathAddon,
    isApplyCustomStyle,
    isEventsGamingAddon,
    isEnableExternalFooter,
    isDisableUserPersistenceAddon,
    isFreeFormTastingAddon,
    isGamifiedSurveyQuestionsAddon,
  } = useAddons();
  const isGameAddon = isEventsGamingAddon || isFreeFormTastingAddon;
  const {
    userImage,
    isUserAuthenticated,
    userBestMatchCharacterForCurrentServiceProductCategory,
    isUserHasCharacterForCurrentPC,
    isUserHasAnyCharacter,
  } = useUser();
  const { discountCoupon, shouldShowDiscountVoucher, shouldShowDiscountBanner, setIsDiscountVoucherShown } =
    useDiscountBanner();

  const { handleResetAndNavigateResultCatalog } = useResultCatalog();
  const { isShowThirdPartyAcceptance } = useThirdParty();
  const { toastMessages, handleClearToastMessage } = useToastMessage();
  const { discoveryQuiz, isDiscoveryQuizProductInstancesLoading } = useDiscoveryQuiz();

  const {
    productCategory,
    locale,
    isTasteIdGuideShown,
    shouldHideDownloadAppCard,
    isTastePathOnboardingShown,
    shouldShowScanBarcode,
    isHostMocked,
  } = useApp();
  const { fidelityCardId, handleFidelityCode } = useFidelityCard();
  const { tastePathJourneyData } = useTastePathJourneyData();

  const { isCharactersLoading } = useCharacters();
  const { isWellnessLoading } = useWellness();
  const { isRecyclingLoading } = useRecycling();
  const { isProductsLoading } = useProducts();
  const iEssenceLoading = useSelector(selectEssenceGroupsLoading);
  const isLoading =
    isCharactersLoading || iEssenceLoading || isWellnessLoading || isRecyclingLoading || isProductsLoading;
  const currentUserCharacterId = userBestMatchCharacterForCurrentServiceProductCategory?.characterId;

  const isHomePage = RouteUtils.currentPageContains(PAGES.vinhood.home);
  const isActionPage = RouteUtils.currentPageContains(PAGES.vinhood.action);
  const isRedirectPage = RouteUtils.currentPageContains(PAGES.vinhood.redirect);
  const isCatalogPage = RouteUtils.currentPageContains(PAGES.vinhood.catalog);
  const isThirdPartyPage = RouteUtils.currentPageContains(PAGES.vinhood.thirdParty);
  const isRegistrationPage = RouteUtils.currentPageContains(PAGES.vinhood.registration);
  const isWidgetLauncherPage = RouteUtils.currentPageContains(PAGES.vinhood.widgetLauncher);
  const isAgeAndGenderPage = RouteUtils.currentPageContains(PAGES.vinhood.ageAndGender);
  const isJournalPage = [PAGES.vinhood.tasteId.tasteIdProductsJournal, PAGES.vinhood.tasteId.tasteIdPlacesJournal].some(
    page => RouteUtils.currentPageContains(page),
  );
  const isPrivacyPage = RouteUtils.currentPageContains(PAGES.vinhood.legal.privacy);

  const { productRateData, isProductRateDataLoading } = useProductRate();
  const producerName = retailerLocationName;
  const { exploreLocationList } = useExploreLocations();
  const { wishlistProductInstanceIds, isWishlistProductListLoading } = useProductList();
  const shouldHideCatalogLink = !isUserHasAnyCharacter || isExternalCharacterPage;

  (function saveExternalReferenceId() {
    const urlParams = new URLSearchParams(window.location.search);
    const externalReferenceIdTag = retailerTags?.[RETAILER_TAG_EXTERNAL_REFERENCE_ID];
    const value = urlParams.get(externalReferenceIdTag);

    if (value) {
      localStorage.setItem(externalReferenceIdTag, value);
    }
  })();

  useEffect(() => {
    if (isApplicationKiosk && fidelityCardId) {
      /** For kiosk users with fidelity cards:
       * Changing loyalty card updates data
       * Changing user character checks availability of promotional products
       * Feature only works if promotional products are available for user's current character */
      dispatch(resetPromotionProducts());

      if (currentUserCharacterId) {
        dispatch(
          fetchPromotionProducts({
            [ApiQueries.character]: currentUserCharacterId,
            [ApiQueries.promotions]: PROMOTION_LABEL_SLUG,
            [ApiQueries.productCategory]: productCategory,
            offset: 0,
          }),
        );
      }
    }
  }, [fidelityCardId, currentUserCharacterId]);

  useEffect(() => {
    if (!isDisableUserPersistenceAddon) return;

    if (localStorage.getItem('disableUserPersistenceFlag') !== 'true') {
      localStorage.setItem('disableUserPersistenceFlag', 'true');
    }
  }, [isDisableUserPersistenceAddon]);

  const shouldShowTasteIdGuide =
    isUserAuthenticated &&
    !isTasteIdGuideShown &&
    [...productRateData, ...wishlistProductInstanceIds].length > 0 &&
    (!isWishlistProductListLoading || !isProductRateDataLoading);

  const handleSetTasteIdPageShown = () =>
    [...productRateData, ...wishlistProductInstanceIds].length > 0 &&
    (!isWishlistProductListLoading || !isProductRateDataLoading) &&
    dispatch(setIsTasteIdGuideShown(true));

  useEffect(() => {
    let timer;
    if (shouldShowTasteIdGuide) {
      timer = setTimeout(handleSetTasteIdPageShown, 10000);
    }
    return () => clearTimeout(timer);
  }, [shouldShowTasteIdGuide]);

  const shouldHideHeader =
    (!isUserAuthenticated && isRegistrationPage) ||
    isThirdPartyPage ||
    isEnableExternalFooter ||
    isGamifiedSurveyQuestionsAddon;
  const shouldHideAnonymousFooter =
    isUserAuthenticated ||
    isUserHasAnyCharacter ||
    isThirdPartyPage ||
    isFromSharedLink ||
    isGameAddon ||
    isGamifiedSurveyQuestionsAddon ||
    isEnableExternalFooter;
  const shouldShowAnonymousFooterWave = (isHomePage && !isUserAuthenticated) || isEnableExternalFooter;
  const shouldHideLoggedFooter =
    (!isUserAuthenticated && !isUserHasAnyCharacter) ||
    isRegistrationPage ||
    isAnonymousRetailerAccess ||
    isThirdPartyPage ||
    isFromSharedLink ||
    isGameAddon ||
    isGamifiedSurveyQuestionsAddon ||
    isEnableExternalFooter;

  const shouldHideExternalFooter = !isEnableExternalFooter;

  const shouldShowDownloadAppCard = !shouldHideDownloadAppCard && isEnableAppBanner && isCatalogPage;
  const isToShowThirdPartyModal = isUserAuthenticated && isShowThirdPartyAcceptance;
  const isEnableLanguageSwitcher = !isUserAuthenticated && retailerLanguages.length > 1;
  const isMultipleProducer = exploreLocationList.length > 1;
  const shouldShowTastePathOnboarding = isEnableTastePathAddon && !isTastePathOnboardingShown && isCatalogPage;
  const customHeaderLogo = isApplyCustomStyle ? retailerLocationCustomStyle?.logos?.company || '' : '';
  const customFooterLogo = isApplyCustomStyle ? retailerLocationCustomStyle?.logos?.powered_by || '' : '';

  const handleLogoClick = isGameAddon
    ? null
    : () => {
        const shouldNavigateUserToTest = isDisableHomeAddon && isUserAuthenticated && isCatalogPage;

        const url = shouldNavigateUserToTest ? PAGES.vinhood.quiz.chooseExpert : PAGES.vinhood.home;
        return !isFromSharedLink ? navigate(prependBasename(url)) : null;
      };
  const handleSettingPageNavigation = () => navigate(prependBasename(PAGES.vinhood.tasteId.tasteIdSettings));

  const handleProductPageNavigation = (productId: string) =>
    navigate(
      prependBasename(PAGES.vinhood.product, {
        [BrowserUrlConsts.productId]: productId,
      }),
    );

  const handleHideDownloadAppClick = () => dispatch(setShouldHideDownloadAppCard());

  const handleDownloadAppClick = () => {
    MixpanelTracker.events.bannerClick(
      'NL0057',
      'Download app',
      {
        identifier: 'R00019',
        route: 'hint',
      },
      {
        identifier: 'R00014',
        route: 'catalog',
      },
    );
    openModal(MODALS.DOWNLOAD_APP_MODAL);
  };

  const handleSelectLanguage = (selectedLanguageCode: string) => dispatch(setServiceLocale(selectedLanguageCode));

  const handleHideTastePathOnboarding = () => dispatch(setIsTastePathOnboardingShown(true));

  const handleShowScanBarcode = () => dispatch(toggleShowScanBarcode(false));
  const handleRetailerPrivacyLink = () => {
    navigate(prependBasename(PAGES.vinhood.legal.privacy));
  };

  const handleSearchCatalogNavigation = () => {
    MixpanelTracker.events.searchManuallyClick();
    navigate(prependBasename(PAGES.vinhood.searchCatalog), {
      state: { shouldHideBackBtn: true },
    });
  };

  const handleSelectGameProduct = (product: TProductInstance) => {
    dispatch(actionSelectGameProduct(product));
    navigate(prependBasename(PAGES.vinhood.action));
  };

  return isRedirectPage || isWidgetLauncherPage ? (
    <>{children}</>
  ) : (
    <RootElement isMocked={isHostMocked}>
      {isLoading && !isAgeAndGenderPage ? (
        <LoadingSpinner {...(!isLocaleReady ? { variant: LOADING_SPINNER_VARIANTS.PLAIN } : {})} />
      ) : (
        <HeaderFooterWidget
          customFooterLogo={customFooterLogo}
          customHeaderLogo={customHeaderLogo}
          discountCoupon={discountCoupon}
          handleClearToastMessage={handleClearToastMessage}
          handleDownloadAppClick={handleDownloadAppClick}
          handleHideDownloadAppClick={handleHideDownloadAppClick}
          handleHideTastePathOnboarding={handleHideTastePathOnboarding}
          handleLogoClick={handleLogoClick}
          handleResetAndNavigateResultCatalog={handleResetAndNavigateResultCatalog}
          handleRetailerPrivacyLink={handleRetailerPrivacyLink}
          handleSelectLanguage={handleSelectLanguage}
          handleSetTasteIdPageShown={handleSetTasteIdPageShown}
          handleSettingPageNavigation={handleSettingPageNavigation}
          isEnableExitNavigation={false}
          isEnableLanguageSwitcher={isEnableLanguageSwitcher}
          isEnableSettingsNavigation={isUserAuthenticated}
          isFixedBody={isJournalPage}
          isHideGearIcon={isAppInIframe}
          isMultipleProducer={isMultipleProducer}
          isPrivacyPage={isPrivacyPage}
          isShowLanguageSwitcher={isHomePage || isActionPage}
          locale={locale}
          producerName={producerName}
          productCategory={productCategory}
          profileImageUrl={userImage}
          retailerLanguages={retailerLanguages}
          retailerName={retailerName}
          shouldHideAnonymousFooter={shouldHideAnonymousFooter}
          shouldHideCatalogLink={shouldHideCatalogLink}
          shouldHideExploreLink={!retailerLocationProducerId}
          shouldHideExternalFooter={shouldHideExternalFooter}
          shouldHideHeader={shouldHideHeader}
          shouldHideHomeLink={isDisableHomeAddon}
          shouldHideLoggedFooter={shouldHideLoggedFooter}
          shouldHideTasteIdLink={!isUserHasCharacterForCurrentPC}
          shouldShowAnonymousFooterWave={shouldShowAnonymousFooterWave}
          shouldShowDiscountBanner={shouldShowDiscountBanner}
          shouldShowDownloadAppCard={shouldShowDownloadAppCard}
          shouldShowHeaderDropDownMenu={false}
          shouldShowTasteIdGuide={shouldShowTasteIdGuide}
          shouldShowTastePathOnboarding={shouldShowTastePathOnboarding}
          tastePathJourneyData={tastePathJourneyData}
          toastMessages={toastMessages}
        >
          {children}

          {shouldShowScanBarcode && (
            <ScannerBarcode
              handleClose={handleShowScanBarcode}
              handleFidelityCode={handleFidelityCode}
              handleProductPageNavigation={handleProductPageNavigation}
              handleSearchCatalogNavigation={handleSearchCatalogNavigation}
              handleSelectGameProduct={handleSelectGameProduct}
              isFreeFormTastingAddon={isFreeFormTastingAddon}
              productCategory={productCategory}
            />
          )}

          {isToShowThirdPartyModal && <ThirdPartyConnectModalContainer />}

          {shouldShowDiscountVoucher && (
            <ModalWrapper
              isAnimated
              isOpen
              withoutDefaultPadding
              justifyModalContent="center"
              minModalContentHeight="100%"
              modalContentWidth="fit-content"
            >
              <VoucherBanner discountCoupon={discountCoupon} setIsDiscountVoucherShown={setIsDiscountVoucherShown} />
            </ModalWrapper>
          )}

          {isDiscoveryQuizModalOpened && discoveryQuiz && (
            <DiscoveryQuizModal
              discoveryQuiz={discoveryQuiz}
              hideModal={() => handleToggleModal(MODALS.DISCOVERY_QUIZ_MODAL)}
              isDisabled={isDiscoveryQuizProductInstancesLoading}
              isModalOpen={isDiscoveryQuizModalOpened}
            />
          )}
        </HeaderFooterWidget>
      )}
    </RootElement>
  );
};
